/**
 * Vipupisteet v2 — the shortlist for day two, read from the map's structure.
 *
 * Own module beside v1's PivotPointsScreen (CLAUDE.md, "Redesigns"), built on
 * the same architecture the room already knows: controls in the top bar,
 * the list in the side tab, a card that opens into detail, a body that
 * scrolls. Runs the v2 discovery in the RAG service, shows each candidate
 * with its evidence, and lets the Kuutio's facilitator accept, dismiss or
 * restore — a candidate is a proposal until someone says otherwise.
 *
 * Two views of the same pivots: Kortit (the queue) and Kartta (the compass,
 * #594). Seinä strips the controls for the wall; the pulse row is its caption.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useSearchParams } from 'react-router-dom';
import { Target } from 'lucide-react';
import MainViewHeader from '../components/MainViewHeader';
import { useDrawer, TAB_BAR_WIDTH, DRAWER_WIDTH_RESERVE_MAX } from '../components/DrawerSystem';
import { useInstance } from '../contexts/InstanceContext';
import useInstanceGrant from '../hooks/useInstanceGrant';
import API_ENDPOINTS from '../config/api';
import socketService from '../../../../packages/shared/socket';
import PivotCard from './PivotCard';
import PivotToolbar, { Toggle } from './PivotToolbar';
import PivotDrawerContent from './PivotDrawerContent';
import PivotDetailModal from './PivotDetailModal';
import PivotCompass from './compass/PivotCompass';
import CompassLegend from './compass/CompassLegend';

// The page owns its own scrolling: /pivotpoints is a no-scroll route because
// v1 is a canvas, so the list cannot rely on the document scrolling.
const Page = styled.div`
  height: var(--kuutio-vh, 100vh); background: #0f0f16; color: #e8e8ef; display: flex; flex-direction: column; overflow: hidden;
`;
// While a tab is open the list gives the drawer its width, the way the map
// and SolutionsScreen do, so the drawer narrows the page instead of covering it.
const reserve = (p) => (p.$drawerOpen ? Math.min(p.$drawerWidth ?? DRAWER_WIDTH_RESERVE_MAX, DRAWER_WIDTH_RESERVE_MAX) + TAB_BAR_WIDTH : TAB_BAR_WIDTH);
const Body = styled.main`
  flex: 1; min-height: 0; overflow-y: auto; width: 100%;
  padding: 20px 20px 80px; display: flex; flex-direction: column; gap: 12px;
  padding-right: ${(p) => reserve(p) + 20}px;
  & > * { max-width: 760px; width: 100%; margin: 0 auto; }
`;
// The compass fills what the drawer leaves; the legend sits under it.
const MapBody = styled.main`
  flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 8px 12px 8px;
  padding-right: ${(p) => reserve(p) + 12}px;
`;
const MapPane = styled.div`flex: 1; min-height: 0;`;
const MapRow = styled.div`flex: 1; min-height: 0; display: flex; gap: 8px; align-items: stretch;`;
const Caption = styled.div`display: flex; gap: 16px; flex-wrap: wrap; font-size: ${(p) => (p.$wall ? '1.05rem' : '0.8rem')}; color: #9ca3af; padding: ${(p) => (p.$wall ? '8px 16px 12px' : '4px 4px 0')}; b { color: #e8e8ef; font-weight: 600; }`;
const LaneHead = styled.div`
  margin: 14px 0 2px; padding: 8px 12px; border-left: 3px solid #f0b35e; color: #c9cbd6; font-size: 0.86rem; line-height: 1.4;
  b { color: #f0b35e; letter-spacing: 0.04em; }
`;
const Empty = styled.p`color: #9ca3af; text-align: center; padding: 40px 0;`;
const Err = styled.div`color: #f87171; font-size: 0.9rem;`;

const MARKS_MS = 30_000;

export default function PivotPointsV2Screen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { instanceId: contextInstanceId, currentInstance } = useInstance();
  const instanceId = searchParams.get('instance') || contextInstanceId;
  const canDecide = useInstanceGrant(instanceId, 'facilitate'); // the run and the decisions are the facilitator's (#1227)
  const { registerTab, unregisterTab, openTab, closeDrawer, activeTab, drawerWidthPx } = useDrawer();

  const [run, setRun] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [map, setMap] = useState({ nodes: {}, loops: [], connections: [] }); // the map, for members, arcs and valence
  const [scenarios, setScenarios] = useState([]);                              // as the scenarios endpoint returns them, wall order
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState(() => (searchParams.get('view') === 'map' ? 'map' : 'cards'));
  const [filter, setFilter] = useState('all');   // all | proposed | accepted
  const [showMembers, setShowMembers] = useState(false);
  const [highlight, setHighlight] = useState(null);   // a legend row's key, or null
  const [wall, setWall] = useState(() => searchParams.get('wall') === '1');
  const [selectedId, setSelectedId] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [marks, setMarks] = useState({});        // id → 'new' | 'grown', after a run, for a while
  const cardRefs = useRef({});
  const marksTimer = useRef(null);

  // Contested until a person has judged: a new run is compared with the one
  // on screen, so a pivot the room built since is announced and a grown one
  // gets its delta ring (#594). The layout never re-runs for this.
  const applyRun = useCallback((next, prev) => {
    if (prev.length) {
      const before = new Map(prev.map((c) => [c.representativeId || c.id, c]));
      const m = {};
      for (const c of next) {
        const old = before.get(c.representativeId || c.id);
        if (!old) m[c.id] = 'new';
        else if ((c.convergence?.value || 0) > (old.convergence?.value || 0)) m[c.id] = 'grown';
      }
      setMarks(m);
      clearTimeout(marksTimer.current);
      marksTimer.current = setTimeout(() => setMarks({}), MARKS_MS);
    }
    setCandidates(next);
  }, []);
  useEffect(() => () => clearTimeout(marksTimer.current), []);

  const load = useCallback(async (prev = []) => {
    if (!instanceId) return;
    try {
      const r = await fetch(API_ENDPOINTS.RAG_PIVOTS_V2_LATEST(instanceId));
      const d = r.ok ? await r.json() : { run: null, candidates: [] };
      setRun(d.run); applyRun(d.candidates || [], prev);
    } catch (e) { setError(`Vipupisteiden haku epäonnistui: ${e.message}`); }
  }, [instanceId, applyRun]);
  useEffect(() => { load(); }, [load]);

  // The map, once: nodes for members and valence, loops and connections for
  // the arcs the compass draws between pivots.
  useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;
    Promise.all([
      fetch(API_ENDPOINTS.PATHWAYS(`kid=${instanceId}`)).then((r) => (r.ok ? r.json() : {})),
      fetch(API_ENDPOINTS.INSTANCE_SCENARIOS(instanceId)).then((r) => (r.ok ? r.json() : { scenarios: [] })),
    ]).then(([p, s]) => {
      if (cancelled) return;
      setMap({ nodes: Object.fromEntries((p.pathways || []).map((n) => [n.id, n])), loops: p.loops || [], connections: p.connections || [] });
      setScenarios(s.scenarios || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [instanceId]);

  // Kartan pulssi (PRD §6b): the derived tier's summary, seeded from the
  // instance and kept current by the tick. A count for the caption — the
  // list itself moves only when the facilitator runs it.
  const [pulse, setPulse] = useState(() => currentInstance?.metadata?.pulse || null);
  useEffect(() => { if (currentInstance?.metadata?.pulse) setPulse(currentInstance.metadata.pulse); }, [currentInstance?.metadata?.pulse]);
  useEffect(() => {
    const sock = socketService.socket;
    if (!sock) return undefined;
    const onTick = (payload) => { if (payload?.instanceId === instanceId && payload.pulse) setPulse(payload.pulse); };
    sock.on('map-pulse-tick', onTick);
    return () => sock.off('map-pulse-tick', onTick);
  }, [instanceId]);

  const discover = async () => {
    setBusy(true); setError(null);
    try {
      const r = await fetch(API_ENDPOINTS.RAG_PIVOTS_V2_RUN(), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instanceId, limit: 12, createdBy: searchParams.get('participant') || 'facilitator' }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `HTTP ${r.status}`); }
      await load(candidates);
    } catch (e) { setError(`Haku epäonnistui: ${e.message}`); } finally { setBusy(false); }
  };

  const setStatus = async (c, status) => {
    const r = await fetch(API_ENDPOINTS.RAG_PIVOTS_V2_CANDIDATE(c.id), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
    });
    if (r.ok) setCandidates((prev) => prev.map((x) => (x.id === c.id ? { ...x, status } : x)));
    else setError(`Tilan tallennus epäonnistui (HTTP ${r.status}).`);
  };

  // A chip opens the map on those nodes: Kaikki view, focus on the first.
  const mapHref = useCallback((ids) => `/impactmap?instance=${instanceId}&type=3&focus=${encodeURIComponent((ids || []).join(','))}`, [instanceId]);
  const when = run?.created_at ? new Date(run.created_at).toLocaleString('fi-FI', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;
  const visible = useMemo(() => candidates.filter((c) => filter === 'all' || c.status === filter), [candidates, filter]);
  // The queue in two lanes (§10). Yhteinen first, as before; rakenteellinen
  // under its own heading, only when the run found any.
  const lanes = useMemo(() => [
    { key: 'yhteinen', items: visible.filter((c) => c.lane !== 'rakenteellinen') },
    { key: 'rakenteellinen', items: visible.filter((c) => c.lane === 'rakenteellinen') },
  ], [visible]);
  // What the leverage reading rests on, as the run saw the map.
  const structure = run?.parameters?.structure || null;
  const structureLine = structure ? (
    structure.leverageIsStructural
      ? <>Kartalla <b>{structure.loops}</b> kierrettä · <b>{structure.crossLinks}</b> ristiinyhteyttä · <b>{structure.blockers}</b> estettä · arvioista <b>{structure.judged ? Math.round((100 * structure.humanJudged) / structure.judged) : 0} %</b> huoneen</>
      : <>Kierteitä, ristiinyhteyksiä tai esteitä ei ole vielä hyväksytty — vipuvoima on vasta yhteneväisyyttä</>
  ) : null;
  const scenarioName = useCallback((id) => scenarios.find((s) => s.id === id)?.title, [scenarios]);

  // Select from anywhere — the side tab, the card, the compass — and bring the card into view.
  const select = useCallback((id) => {
    setSelectedId(id);
    const el = cardRefs.current[id];
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, []);

  // Seinä is a URL state too, so a wall can be opened straight into it; Esc
  // leaves it. The wall shows the map alone, so the drawer closes with it.
  const toggleWall = useCallback((on) => {
    setWall(on);
    if (on) closeDrawer();
    setSearchParams((prev) => { const p = new URLSearchParams(prev); if (on) p.set('wall', '1'); else p.delete('wall'); return p; }, { replace: true });
  }, [setSearchParams, closeDrawer]);
  useEffect(() => {
    if (!wall) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') toggleWall(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [wall, toggleWall]);

  // The side tab, registered the way v1 registers its list (registerTab merges).
  useEffect(() => () => unregisterTab('pivotpoints'), [unregisterTab]);
  useEffect(() => {
    registerTab({
      id: 'pivotpoints',
      label: `Vipupisteet (${candidates.length})`,
      icon: <Target size={18} />,
      color: 'rgba(124, 58, 237, 0.3)',
      borderColor: 'rgba(167, 139, 250, 0.5)',
      order: 10,
      render: () => (
        <PivotDrawerContent candidates={candidates} run={run} when={when} selectedId={selectedId} onSelect={select} />
      ),
    });
  }, [registerTab, candidates, run, when, selectedId, select]);
  // Open the tab on first arrival, like v1 does, so the list is where people expect it — not on a wall.
  const openedOnce = useRef(false);
  useEffect(() => {
    if (!openedOnce.current && candidates.length && !activeTab && !wall) { openedOnce.current = true; openTab('pivotpoints'); }
  }, [candidates.length, activeTab, openTab, wall]);

  const detail = detailId ? candidates.find((c) => c.id === detailId) : null;
  const detailMembers = detail ? (detail.memberIds || []).map((id) => map.nodes[id]).filter(Boolean) : [];
  const question = currentInstance?.settings?.mainQuestion || currentInstance?.settings?.workshopGoal || currentInstance?.title || '';
  const caption = (
    <Caption $wall={wall}>
      <span>{when ? <>Viimeisin ajo <b>{when}</b> · <b>{candidates.length}</b> ehdokasta</> : 'Ei vielä ajoa'}</span>
      {pulse && <span>💓 Kartalta nyt <b>{pulse.candidates}</b> ehdokasta{pulse.newSince ? <> · <b>{pulse.newSince}</b> uutta viime ajon jälkeen</> : ''}</span>}
      {!wall && (structureLine || <span>{map.loops.length} silmukkaa kartalla</span>)}
    </Caption>
  );

  return (
    <Page>
      {!wall && (
        <MainViewHeader
          activeView="pivot_points"
          instanceTitle={currentInstance?.title || ''}
          subtitleExtra={<span style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: 6, background: 'rgba(124,58,237,0.3)', color: '#e9d5ff' }}>V2</span>}
          inDocumentFlow
          hideStatusDot
        >
          <PivotToolbar view={view} onViewChange={setView} filter={filter} onFilterChange={setFilter} canRun={canDecide && !!instanceId} busy={busy} onRun={discover}>
            {view === 'map' && (
              <>
                <Toggle type="button" $on={showMembers} aria-pressed={showMembers} title="Näytä vipupisteiden jäsenet ja niiden väliset ketjut" onClick={() => setShowMembers((v) => !v)}>Jäsenet</Toggle>
                <Toggle type="button" $on={false} title="Seinänäkymä: ei ohjaimia, isot otsikot (Esc palaa)" onClick={() => toggleWall(true)}>Seinä</Toggle>
              </>
            )}
          </PivotToolbar>
        </MainViewHeader>
      )}
      {view === 'map' || wall ? (
        <MapBody $drawerOpen={!!activeTab} $drawerWidth={drawerWidthPx}>
          {error && <Err>{error}</Err>}
          {!candidates.length && !busy && <Empty>Ei vielä ajoa. Etsi vipupisteet, kun kartan silmukat, ristiinyhteydet ja esteet on hyväksytty — vipuvoima luetaan niistä.</Empty>}
          <MapRow>
            {!wall && <CompassLegend scenarioColors={scenarios.map((s) => s.color).filter(Boolean)} highlight={highlight} onHighlight={setHighlight} />}
            <MapPane>
              <PivotCompass
                candidates={visible} nodesById={map.nodes} scenarios={scenarios} loops={map.loops} connections={map.connections}
                question={question} selectedId={selectedId} onSelect={(id) => { select(id); if (id && !wall) setDetailId(id); }}
                showMembers={showMembers} wall={wall} marks={marks} highlight={wall ? null : highlight}
              />
            </MapPane>
          </MapRow>
          {caption}
        </MapBody>
      ) : (
        <Body $drawerOpen={!!activeTab} $drawerWidth={drawerWidthPx}>
          {caption}
          {error && <Err>{error}</Err>}
          {!candidates.length && !busy && <Empty>Ei vielä ajoa. Etsi vipupisteet, kun kartan silmukat, ristiinyhteydet ja esteet on hyväksytty — vipuvoima luetaan niistä.</Empty>}
          {lanes.map((lane) => lane.items.length > 0 && (
            <React.Fragment key={lane.key}>
              {lane.key === 'rakenteellinen' && (
                <LaneHead>
                  <b>Rakenteellinen</b> — vipuvoima ilman yhteneväisyyttä: yksi kirjoittaja, mutta kierteellä, valintakohdassa tai esteen takana. Ehdotuksia siinä missä muutkin.
                </LaneHead>
              )}
              {lane.items.map((c) => (
                <PivotCard
                  key={c.id}
                  c={c}
                  mapHref={mapHref}
                  onStatus={canDecide ? setStatus : null}
                  selected={c.id === selectedId}
                  onSelect={(x) => { select(x.id); setDetailId(x.id); }}
                  cardRef={(el) => { cardRefs.current[c.id] = el; }}
                />
              ))}
            </React.Fragment>
          ))}
        </Body>
      )}
      {detail && (
        <PivotDetailModal
          c={detail}
          members={detailMembers}
          scenarioName={scenarioName}
          mapHref={mapHref}
          onStatus={setStatus}
          canDecide={canDecide}
          onClose={() => setDetailId(null)}
        />
      )}
    </Page>
  );
}
